import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useStore } from "../../../helpers";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CreateWebLinkRequest,
  UpdateWebLinkRequest,
  ListWebLinksResponse,
} from "../../../stores/active-survey-store/weblink-types";

// Query Keys
export const weblinkKeys = {
  all: ["weblinks"] as const,
  lists: () => [...weblinkKeys.all, "list"] as const,
  list: (projectid: number, offset?: number, count?: number) => 
    [...weblinkKeys.lists(), projectid, offset ?? 0, count ?? 25] as const,
  users: () => [...weblinkKeys.all, "users"] as const,
  usersList: (projectid: number, query: string) => [...weblinkKeys.users(), projectid, query] as const,
};

// Query: List WebLinks
export const useWebLinksList = (
  projectid: number | string | undefined,
  offset: number = 0,
  count: number = 25
) => {
  const { activeSurveyStore } = useStore();

  return useQuery({
    queryKey: weblinkKeys.list(Number(projectid), offset, count),
    queryFn: async (): Promise<ListWebLinksResponse | null> => {
      if (!projectid) return null;
      return await activeSurveyStore.listWebLinks(Number(projectid), offset, count);
    },
    enabled: !!projectid,
    refetchOnWindowFocus: false,
  });
};

// Query: Search Users
export const useSearchUsers = (projectid: number | string | undefined, query: string = "") => {
  const { projectStore } = useStore();

  return useQuery({
    queryKey: weblinkKeys.usersList(Number(projectid), query),
    queryFn: async () => {
      if (!projectid) return { users: [] };
      const payload = {
        projectid: Number(projectid),
        tags: [],
        query: query,
        // Use limit 10 for initial load (empty query), 500 for searches
        limit: query === "" ? 10 : 500,
        offset: 0,
      };
      const response = await projectStore.getAllUsers(payload);
      if (response.ok && response.data?.users) {
        return { users: response.data.users };
      }
      return { users: [] };
    },
    enabled: !!projectid,
    refetchOnWindowFocus: false,
  });
};

// Mutation: Create WebLink
export const useCreateWebLink = () => {
  const { activeSurveyStore } = useStore();
  const queryClient = useQueryClient();
  const { projectid } = useParams<{ projectid: string }>();

  return useMutation({
    mutationFn: async (payload: CreateWebLinkRequest): Promise<boolean> => {
      return await activeSurveyStore.createWebLink(payload);
    },
    onSuccess: (success, payload) => {
      if (success) {
        toast.success("WebLink created successfully");
        activeSurveyStore.setCreateWebLinkModalOpen(false);
        // Invalidate and refetch weblinks list (all pagination queries for this project)
        queryClient.invalidateQueries({ 
          queryKey: weblinkKeys.lists(),
          exact: false 
        });
      } else {
        toast.error("Failed to create WebLink");
      }
    },
    onError: (error) => {
      console.error("Error creating weblink:", error);
      toast.error("An error occurred while creating the WebLink");
    },
  });
};

// Mutation: Update WebLink
export const useUpdateWebLink = () => {
  const { activeSurveyStore } = useStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<UpdateWebLinkRequest>;
    }): Promise<boolean> => {
      return await activeSurveyStore.updateWebLink(id, updates);
    },
    onSuccess: (success, variables) => {
      if (success) {
        toast.success("WebLink updated successfully");
        activeSurveyStore.setEditWebLinkModalOpen(false);
        activeSurveyStore.setActiveWebLink(null);
        // Find the projectid from the weblink
        const weblink = activeSurveyStore.weblinks.find((w) => w.id === variables.id);
        if (weblink?.projectid) {
          // Invalidate all pagination queries for this project
          queryClient.invalidateQueries({ 
            queryKey: weblinkKeys.lists(),
            exact: false 
          });
        }
      } else {
        toast.error("Failed to update WebLink");
      }
    },
    onError: (error) => {
      console.error("Error updating weblink:", error);
      toast.error("An error occurred while updating the WebLink");
    },
  });
};

// Mutation: Delete WebLink
export const useDeleteWebLink = () => {
  const { activeSurveyStore } = useStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<boolean> => {
      return await activeSurveyStore.deleteWebLink(id);
    },
    onSuccess: (success, id) => {
      if (success) {
        toast.success("WebLink deleted successfully");
        activeSurveyStore.setDeleteWebLinkModalOpen(false);
        activeSurveyStore.setActiveWebLink(null);
        // Find the projectid from the weblink
        const weblink = activeSurveyStore.weblinks.find((w) => w.id === id);
        if (weblink?.projectid) {
          // Invalidate all pagination queries for this project
          queryClient.invalidateQueries({ 
            queryKey: weblinkKeys.lists(),
            exact: false 
          });
        }
      } else {
        toast.error("Failed to delete WebLink");
      }
    },
    onError: (error) => {
      console.error("Error deleting weblink:", error);
      toast.error("An error occurred while deleting the WebLink");
    },
  });
};

// Mutation: Bulk Toggle WebLink Active
export const useBulkToggleWebLinkActive = () => {
  const { activeSurveyStore } = useStore();
  const queryClient = useQueryClient();
  const { projectid } = useParams<{ projectid: string }>();

  return useMutation({
    mutationFn: async ({
      ids,
      active,
    }: {
      ids: number[];
      active: boolean;
    }): Promise<boolean> => {
      return await activeSurveyStore.bulkToggleWebLinkActive(ids, active);
    },
    onSuccess: (success, variables) => {
      if (success) {
        toast.success(`${variables.ids.length} link(s) ${variables.active ? "activated" : "deactivated"} successfully`);
        activeSurveyStore.setSelectedWebLinkIds([]);
        if (projectid) {
          // Invalidate all pagination queries for this project
          queryClient.invalidateQueries({ 
            queryKey: weblinkKeys.lists(),
            exact: false 
          });
        }
      } else {
        toast.error(`Failed to ${variables.active ? "activate" : "deactivate"} some links`);
      }
    },
    onError: (error) => {
      console.error("Error bulk toggling weblink active status:", error);
      toast.error("An error occurred");
    },
  });
};

// Mutation: Bulk Delete WebLinks
export const useBulkDeleteWebLinks = () => {
  const { activeSurveyStore } = useStore();
  const queryClient = useQueryClient();
  const { projectid } = useParams<{ projectid: string }>();

  return useMutation({
    mutationFn: async (ids: number[]): Promise<boolean> => {
      return await activeSurveyStore.bulkDeleteWebLinks(ids);
    },
    onSuccess: (success, ids) => {
      if (success) {
        toast.success(`${ids.length} link(s) deleted successfully`);
        activeSurveyStore.setSelectedWebLinkIds([]);
        if (projectid) {
          // Invalidate all pagination queries for this project
          queryClient.invalidateQueries({ 
            queryKey: weblinkKeys.lists(),
            exact: false 
          });
        }
      } else {
        toast.error("Failed to delete some links");
      }
    },
    onError: (error) => {
      console.error("Error bulk deleting weblinks:", error);
      toast.error("An error occurred");
    },
  });
};

